import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
          <Link to="/privacy" className="flex items-center gap-2 text-slate-400 hover:text-[#FF5F00] transition-colors">
            <Shield className="w-5 h-5" />
            <span>Privacy Policy</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-[#FF5F00]/10 rounded-xl">
            <FileText className="w-8 h-8 text-[#FF5F00]" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Terms of Service</h1>
            <p className="text-slate-400">Effective Date: February 15, 2026</p>
          </div>
        </div>

        <div className="prose prose-invert prose-slate max-w-none">
          {/* Section 1.1 */}
          <section className="mb-10 bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <span className="text-[#FF5F00]">1.1</span> The Service
            </h2>
            <p className="text-slate-300 leading-relaxed">
              MyJobTools provides a platform for job tracking, scheduling, and invoicing. By using our service, you agree to these terms.
            </p>
          </section>

          {/* Section 1.2 */}
          <section className="mb-10 bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <span className="text-[#FF5F00]">1.2</span> No Professional Advice
            </h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              MyJobTools is a software tool only.
            </p>
            <ul className="space-y-3 text-slate-300">
              <li className="flex items-start gap-3">
                <span className="text-[#FF5F00] mt-1">•</span>
                <span>We are <strong className="text-white">not</strong> an accountancy firm, tax advisor, or financial consultancy.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#FF5F00] mt-1">•</span>
                <span>We provide a centralized location to house and track data, but we do not audit, verify, or provide professional opinions on the accuracy of your financial records.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#FF5F00] mt-1">•</span>
                <span><strong className="text-white">Recommendation:</strong> You should consult with a qualified tax professional or certified public accountant (CPA) regarding your business expenses, tax filings, and financial strategy.</span>
              </li>
            </ul>
          </section>

          {/* Section 1.3 */}
          <section className="mb-10 bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <span className="text-[#FF5F00]">1.3</span> User Content & Responsibility
            </h2>
            <ul className="space-y-3 text-slate-300">
              <li className="flex items-start gap-3">
                <span className="text-[#FF5F00] mt-1">•</span>
                <span><strong className="text-white">Ownership:</strong> You retain all rights to the images, receipts, and data you upload.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#FF5F00] mt-1">•</span>
                <span><strong className="text-white">Accuracy:</strong> You are solely responsible for the accuracy of the data entered. MyJobTools is not liable for errors in invoices or expense tracking resulting from incorrect user input.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#FF5F00] mt-1">•</span>
                <span><strong className="text-white">Compliance:</strong> You agree not to upload illegal or infringing material.</span>
              </li>
            </ul>
          </section>

          {/* Section 1.4 */}
          <section className="mb-10 bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <span className="text-[#FF5F00]">1.4</span> Fees and Subscription
            </h2>
            <ul className="space-y-3 text-slate-300">
              <li className="flex items-start gap-3">
                <span className="text-[#FF5F00] mt-1">•</span>
                <span>MyJobTools is a recurring revenue (SaaS) model. Subscriptions automatically renew unless canceled via your account settings.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#FF5F00] mt-1">•</span>
                <span>We reserve the right to change pricing with 30 days' notice.</span>
              </li>
            </ul>
          </section>

          {/* Section 1.5 */}
          <section className="mb-10 bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <span className="text-[#FF5F00]">1.5</span> Limitation of Liability
            </h2>
            <p className="text-slate-300 leading-relaxed">
              To the maximum extent permitted by law, MyJobTools shall not be liable for any indirect, incidental, or consequential damages (including loss of profits or data) arising from your use of the service.
            </p>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-slate-700 flex flex-col sm:flex-row gap-4 justify-between items-center">
          <Link to="/privacy" className="text-[#FF5F00] hover:text-[#FF5F00]/80 transition-colors flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Read our Privacy Policy
          </Link>
          <Link to="/register" className="bg-[#FF5F00] text-white px-6 py-3 rounded-lg hover:bg-[#FF5F00]/90 transition-colors font-medium">
            Get Started Free
          </Link>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
